import React from "react";
import {
  AppBar,
  Box,
  Button,
  Chip,
  Stack,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";

const NavBtn = ({ to, label }) => {
  const { pathname } = useLocation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const active = pathname === to;

  return (
    <Button
      component={Link}
      to={to}
      sx={{
        borderRadius: 999,
        px: 2,
        py: 0.6,
        textTransform: "none",
        fontWeight: 700,
        color: active
          ? isDark
            ? "rgba(255,255,255,0.95)"
            : "rgba(15,23,42,0.9)"
          : isDark
          ? "rgba(255,255,255,0.7)"
          : "rgba(15,23,42,0.7)",
        border: active
          ? isDark
            ? "1px solid rgba(255,255,255,0.22)"
            : "1px solid rgba(15,23,42,0.16)"
          : "1px solid transparent",
        background: active
          ? isDark
            ? "linear-gradient(135deg, rgba(56,189,248,0.32), rgba(251,191,36,0.18))"
            : "linear-gradient(135deg, rgba(37,99,235,0.12), rgba(14,116,144,0.08))"
          : "transparent",
        "&:hover": {
          background: isDark
            ? "rgba(255,255,255,0.08)"
            : "rgba(15,23,42,0.06)",
        },
      }}
    >
      {label}
    </Button>
  );
};

export default function PublicLayout({ children }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { isAuthenticated, isAdmin, user, logout } = useAuth();

  const appBarBg = isDark
    ? "linear-gradient(180deg, rgba(10,12,24,0.92), rgba(10,12,24,0.82))"
    : "linear-gradient(180deg, rgba(255,255,255,0.94), rgba(248,250,252,0.88))";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: isDark
          ? "radial-gradient(1200px 650px at 10% 10%, rgba(56,189,248,0.18), rgba(0,0,0,0)), radial-gradient(900px 540px at 80% 20%, rgba(251,191,36,0.14), rgba(0,0,0,0)), radial-gradient(900px 600px at 50% 90%, rgba(148,163,184,0.12), rgba(0,0,0,0))"
          : "radial-gradient(1100px 650px at 10% 10%, rgba(37,99,235,0.16), rgba(255,255,255,0)), radial-gradient(900px 540px at 82% 25%, rgba(14,116,144,0.14), rgba(255,255,255,0)), radial-gradient(900px 600px at 50% 95%, rgba(15,23,42,0.06), rgba(255,255,255,0))",
        color: isDark ? "#e2e8f0" : "#0f172a",
        position: "relative",
        overflow: "visible",
      }}
    >
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          top: 0,
          left: 0,
          right: 0,
          zIndex: (t) => t.zIndex.appBar,
          backdropFilter: "blur(16px)",
          background: appBarBg,
          color: isDark ? "#e2e8f0" : "rgba(15,23,42,0.92)",
          borderBottom: isDark
            ? "1px solid rgba(255,255,255,0.08)"
            : "1px solid rgba(15,23,42,0.08)",
        }}
      >
        <Toolbar sx={{ gap: 2, position: "relative" }}>
          <Typography
            variant="body2"
            sx={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              color: "#16a34a",
              fontWeight: 800,
              letterSpacing: 0.3,
              display: { xs: "none", md: "block" },
              pointerEvents: "none",
              whiteSpace: "nowrap",
            }}
          >
            Under Development
          </Typography>

          <Stack spacing={0.1}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                letterSpacing: 0.5,
                fontFamily: '"Newsreader", serif',
                color: isDark ? "rgba(255,255,255,0.96)" : "rgba(15,23,42,0.96)",
              }}
            >
              Passivest
            </Typography>
            <Typography
              variant="caption"
              sx={{
                opacity: 0.85,
                color: isDark ? "rgba(226,232,240,0.82)" : "rgba(15,23,42,0.78)",
              }}
            >
              Let's invest in passives, actively
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ ml: 2 }}>
            <NavBtn to="/" label="Explore" />
            <NavBtn to="/market-data" label="Market Data" />
            <NavBtn to="/rankings" label="ETF Ranking Dashboard" />
            {!isAuthenticated ? <NavBtn to="/login" label="Sign in" /> : null}
          </Stack>

          <Box sx={{ flex: 1 }} />

          <Stack direction="row" spacing={1} alignItems="center">
            {isAuthenticated ? (
              <Chip
                label={`Signed in as ${user?.name || "Investor"}`}
                sx={{
                  borderRadius: 999,
                  fontWeight: 700,
                  background: isDark
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(15,23,42,0.08)",
                }}
              />
            ) : (
              <Chip
                label="Free access"
                sx={{
                  borderRadius: 999,
                  fontWeight: 700,
                  background: isDark
                    ? "rgba(56,189,248,0.18)"
                    : "rgba(37,99,235,0.12)",
                }}
              />
            )}

            {isAdmin ? (
              <Button component={Link} to="/admin" variant="contained">
                Admin
              </Button>
            ) : null}

            {isAuthenticated ? (
              <Button onClick={logout} variant="outlined" sx={{ borderRadius: 999 }}>
                Sign out
              </Button>
            ) : (
              <Button
                component={Link}
                to="/register"
                variant="contained"
                sx={{ borderRadius: 999 }}
              >
                Start free
              </Button>
            )}
          </Stack>
        </Toolbar>
      </AppBar>

      <Toolbar />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          px: { xs: 2, md: 6 },
          py: 4,
          flex: 1,
        }}
      >
        {children}
      </Box>

      <Box sx={{ px: { xs: 2, md: 6 }, pb: 4, opacity: 0.7 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
        >
          <Typography variant="caption">
            Passivest (c) 2026. Built for smart, calm investing.
          </Typography>
          <Stack direction="row" spacing={2}>
            <Typography variant="caption">Privacy</Typography>
            <Typography variant="caption">Security</Typography>
            <Typography variant="caption">Support</Typography>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}
